import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import TopicCard from "@/components/base/TopicCard";
import { topics, topicCategories } from "@/mocks/topics";
import { usePageMeta } from "@/hooks/usePageMeta";

type SortKey = "最新更新" | "热度最高" | "可信度最高";

const sortOptions: SortKey[] = ["最新更新", "热度最高", "可信度最高"];

export default function TopicsPage() {
  const [params, setParams] = useSearchParams();
  const activeCategory = params.get("category") || "全部";
  const queryFromUrl = params.get("q") || "";
  const [keyword, setKeyword] = useState(queryFromUrl);
  const [sort, setSort] = useState<SortKey>("最新更新");

  usePageMeta({
    title: "爆料追踪列表 - 全部事件证据链与可信度 | 蝌蚪爆料",
    description:
      "浏览蝌蚪爆料全部追踪事件，支持按娱乐动态、社交网络、商业代言、网络传闻与行业风向筛选，并可按热度与可信度排序查看证据链。",
    keywords: "蝌蚪爆料列表,爆料追踪,事件证据链,蝌蚪爆料可信度,蝌蚪爆料真假",
    canonicalPath: "/topics",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "爆料追踪列表",
      url: "https://jiumeijiaju.cn/topics",
      description: "按类别与热度浏览全部追踪事件及其证据链",
      mainEntity: {
        "@type": "ItemList",
        itemListElement: topics.map((topic, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: topic.title,
          url: `https://jiumeijiaju.cn/topic/${topic.id}`,
        })),
      },
    },
  });

  const filtered = useMemo(() => {
    const q = queryFromUrl.trim().toLowerCase();
    let list = topics.filter((topic) => {
      const matchCategory = activeCategory === "全部" || topic.category === activeCategory;
      const matchQuery =
        !q ||
        topic.title.toLowerCase().includes(q) ||
        topic.summary.toLowerCase().includes(q) ||
        topic.tags.some((tag) => tag.toLowerCase().includes(q)) ||
        topic.category.toLowerCase().includes(q);
      return matchCategory && matchQuery;
    });
    list = [...list].sort((a, b) => {
      if (sort === "热度最高") return b.heat - a.heat;
      if (sort === "可信度最高") return b.credibility - a.credibility;
      return b.updated.localeCompare(a.updated);
    });
    return list;
  }, [activeCategory, queryFromUrl, sort]);

  const updateParams = (next: { category?: string; q?: string }) => {
    const merged = new URLSearchParams(params);
    if (next.category !== undefined) {
      if (next.category === "全部") merged.delete("category");
      else merged.set("category", next.category);
    }
    if (next.q !== undefined) {
      if (!next.q) merged.delete("q");
      else merged.set("q", next.q);
    }
    setParams(merged, { replace: true });
  };

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    updateParams({ q: keyword.trim() });
  };

  return (
    <SiteLayout>
      <div className="border-b border-background-300 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <nav aria-label="面包屑" className="flex items-center gap-2 font-label text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-700 cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">爆料追踪</span>
          </nav>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            爆料追踪列表
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            每条记录都附带可核对的依据条目与可信度参考。可按类别筛选、按关键词检索，或切换排序方式查看。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {topicCategories.map((cat) => {
              const isActive = activeCategory === cat;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => updateParams({ category: cat })}
                  className={`whitespace-nowrap rounded-full border px-4 py-1.5 font-label text-sm transition-colors cursor-pointer ${
                    isActive
                      ? "border-primary-500 bg-primary-500 text-background-50"
                      : "border-background-300 bg-background-100 text-foreground-700 hover:border-primary-300"
                  }`}
                >
                  {cat}
                </button>
              );
            })}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <form onSubmit={handleSearch} className="flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2 focus-within:border-primary-400" role="search">
              <i className="ri-search-line text-sm text-foreground-500" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="search"
                placeholder="搜索事件或关键词"
                aria-label="搜索事件或关键词"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400 sm:w-48"
              />
              <button type="submit" className="whitespace-nowrap font-label text-xs text-primary-700 cursor-pointer">
                搜索
              </button>
            </form>

            <div className="flex items-center gap-1 rounded-md border border-background-300 bg-background-100 p-1">
              {sortOptions.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setSort(opt)}
                  className={`whitespace-nowrap rounded-md px-3 py-1.5 font-label text-xs transition-colors cursor-pointer ${
                    sort === opt ? "bg-foreground-950 text-background-50" : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between border-t border-background-300 pt-4">
          <p className="font-label text-xs text-foreground-500">
            共 <span className="font-semibold text-foreground-900">{filtered.length}</span> 条结果
            {activeCategory !== "全部" && ` · 分类：${activeCategory}`}
            {queryFromUrl && ` · 关键词：${queryFromUrl}`}
          </p>
          {(activeCategory !== "全部" || queryFromUrl) && (
            <button
              type="button"
              onClick={() => {
                setKeyword("");
                setParams(new URLSearchParams(), { replace: true });
              }}
              className="inline-flex items-center gap-1 font-label text-xs text-primary-700 hover:text-primary-600 cursor-pointer"
            >
              <i className="ri-refresh-line" /> 清除筛选
            </button>
          )}
        </div>

        {filtered.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((topic) => (
              <TopicCard key={topic.id} topic={topic} />
            ))}
          </div>
        ) : (
          <div className="mt-10 rounded-lg border border-dashed border-background-400 bg-background-100 p-12 text-center">
            <i className="ri-file-search-line text-3xl text-foreground-400" />
            <p className="mt-4 font-heading text-base font-semibold text-foreground-900">
              没有匹配的追踪记录
            </p>
            <p className="mt-2 text-sm text-foreground-600">
              换个关键词试试，或清除筛选条件查看全部事件。
            </p>
            <button
              type="button"
              onClick={() => {
                setKeyword("");
                setParams(new URLSearchParams(), { replace: true });
              }}
              className="mt-5 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 font-label text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              查看全部
            </button>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}